import { useState } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { TABLE_PAGINATION } from '@/util/constants';

export const VOICE_USAGE_PAGE_SIZE = TABLE_PAGINATION;

export type VoiceCarrier = 'twilio' | 'telnyx';

export type VoiceDirection = 'outbound' | 'inbound';

export type VoiceUsageSummaryRow = {
  carrier: VoiceCarrier;
  direction: VoiceDirection;
  calls: number;
  durationSeconds: number;
  billableMinutes: number;
};

export type VoiceCallRow = {
  sessionId: string;
  callId: string;
  // Not narrowed to VoiceCarrier: the list has no carrier filter, so a new carrier shows up here.
  carrier: string;
  direction: VoiceDirection;
  campaignId: string | null;
  endedAt: string;
  durationSeconds: number;
  billableMinutes: number;
};

type CarrierAggregate = {
  aggregate: {
    count: number;
    sum: { duration_seconds: number | null; billable_minutes: number | null };
  };
};

const SUMMARY_ROWS = [
  ['twilio', 'outbound'],
  ['twilio', 'inbound'],
  ['telnyx', 'outbound'],
  ['telnyx', 'inbound'],
] as const;

type VoiceUsageSummaryData = Record<`${VoiceCarrier}_${VoiceDirection}`, CarrierAggregate> & {
  total: { aggregate: { count: number } };
};

type VoiceCallsData = {
  calls: {
    session_id: string;
    call_id: string;
    carrier: string;
    direction: VoiceDirection;
    campaign_id: string | null;
    ended_at: string;
    duration_seconds: number;
    billable_minutes: number;
  }[];
};

/**
 * Totals per carrier and direction for a UTC month window. `total` has no carrier filter, so it
 * counts every row the calls list can page through.
 */
export const GET_VOICE_USAGE_SUMMARY = gql`
  query VoiceUsageSummary($start: timestamp!, $end: timestamp!) {
    twilio_outbound: list_voice_calls_aggregate(
      args: { start_time: $start, end_time: $end }
      where: { carrier: { _eq: "twilio" }, direction: { _eq: "outbound" } }
    ) {
      aggregate {
        count
        sum {
          duration_seconds
          billable_minutes
        }
      }
    }
    twilio_inbound: list_voice_calls_aggregate(
      args: { start_time: $start, end_time: $end }
      where: { carrier: { _eq: "twilio" }, direction: { _eq: "inbound" } }
    ) {
      aggregate {
        count
        sum {
          duration_seconds
          billable_minutes
        }
      }
    }
    telnyx_outbound: list_voice_calls_aggregate(
      args: { start_time: $start, end_time: $end }
      where: { carrier: { _eq: "telnyx" }, direction: { _eq: "outbound" } }
    ) {
      aggregate {
        count
        sum {
          duration_seconds
          billable_minutes
        }
      }
    }
    telnyx_inbound: list_voice_calls_aggregate(
      args: { start_time: $start, end_time: $end }
      where: { carrier: { _eq: "telnyx" }, direction: { _eq: "inbound" } }
    ) {
      aggregate {
        count
        sum {
          duration_seconds
          billable_minutes
        }
      }
    }
    total: list_voice_calls_aggregate(args: { start_time: $start, end_time: $end }) {
      aggregate {
        count
      }
    }
  }
`;

/** One page of calls that ended in a UTC month window. */
export const GET_VOICE_CALLS = gql`
  query VoiceCalls($start: timestamp!, $end: timestamp!, $limit: Int!, $offset: Int!) {
    calls: list_voice_calls(
      args: { start_time: $start, end_time: $end }
      order_by: [{ ended_at: desc }, { carrier: asc }, { call_id: asc }]
      limit: $limit
      offset: $offset
    ) {
      session_id
      call_id
      carrier
      direction
      campaign_id
      ended_at
      duration_seconds
      billable_minutes
    }
  }
`;

const pad = (n: number) => String(n).padStart(2, '0');

const toMonth = (year: number, monthIndex: number) => `${year}-${pad(monthIndex + 1)}`;

const toTimestamp = (date: Date) => date.toISOString().slice(0, 19);

/** `'YYYY-MM'` to a half-open UTC window, as zoneless `YYYY-MM-DDT00:00:00` strings. */
export const getMonthWindow = (month: string) => {
  const [year, monthNumber] = month.split('-').map(Number);
  const next = new Date(Date.UTC(year, monthNumber, 1));
  return {
    start: `${month}-01T00:00:00`,
    end: `${toMonth(next.getUTCFullYear(), next.getUTCMonth())}-01T00:00:00`,
  };
};

/** The current UTC month and the 5 before it, newest first. */
export const getMonthOptions = (now: Date) =>
  Array.from({ length: 6 }, (_, i) => {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    return toMonth(date.getUTCFullYear(), date.getUTCMonth());
  });

const toSummaryRow = (
  carrier: VoiceCarrier,
  direction: VoiceDirection,
  { aggregate }: CarrierAggregate
): VoiceUsageSummaryRow => ({
  carrier,
  direction,
  calls: aggregate.count,
  durationSeconds: aggregate.sum.duration_seconds ?? 0,
  billableMinutes: aggregate.sum.billable_minutes ?? 0,
});

export const useVoiceUsage = ({ month, page }: { month: string; page: number }) => {
  // Pin the window's end per month so calls ending while the user pages do not shift the offsets.
  // ponytail: a call recorded late with an older ended_at still shifts rows; keyset paging fixes it.
  const [pinned, setPinned] = useState(() => ({ month, now: toTimestamp(new Date()) }));
  let { now } = pinned;
  if (pinned.month !== month) {
    now = toTimestamp(new Date());
    setPinned({ month, now });
  }
  const { start, end: monthEnd } = getMonthWindow(month);
  const end = monthEnd < now ? monthEnd : now;
  // Separate from the calls page, so a page turn does not reload the totals or the row count.
  const summaryQuery = useQuery<VoiceUsageSummaryData>(GET_VOICE_USAGE_SUMMARY, {
    variables: { start, end },
  });
  const callsQuery = useQuery<VoiceCallsData>(GET_VOICE_CALLS, {
    variables: { start, end, limit: VOICE_USAGE_PAGE_SIZE, offset: page * VOICE_USAGE_PAGE_SIZE },
  });
  const summaryData = summaryQuery.data;

  const summary = summaryData
    ? SUMMARY_ROWS.map(([carrier, direction]) =>
        toSummaryRow(carrier, direction, summaryData[`${carrier}_${direction}`])
      ).filter((row) => row.calls > 0)
    : [];

  const calls: VoiceCallRow[] = (callsQuery.data?.calls ?? []).map((row) => ({
    sessionId: row.session_id,
    callId: row.call_id,
    carrier: row.carrier,
    direction: row.direction,
    campaignId: row.campaign_id,
    endedAt: row.ended_at,
    durationSeconds: row.duration_seconds,
    billableMinutes: row.billable_minutes,
  }));

  return {
    summary,
    calls,
    totalCalls: summaryData?.total.aggregate.count ?? 0,
    isSummaryLoading: summaryQuery.loading,
    isCallsLoading: callsQuery.loading,
    isError: Boolean(summaryQuery.error || callsQuery.error),
  };
};

/** The Usage page is for whoever pays the carrier bill or investigates calls. */
export const useCanReadUsage = () =>
  useSelector(
    (state: RootState) =>
      getPermissions(state, 'billing', actions.READ) ||
      getPermissions(state, 'analytics', actions.READ)
  );

export const GET_HAS_VOICE_CALLS = gql`
  query HasVoiceCalls($start: timestamp!, $end: timestamp!) {
    list_voice_calls(args: { start_time: $start, end_time: $end }, limit: 1) {
      call_id
    }
  }
`;

/**
 * Whether any month in the picker has a carrier call. `undefined` while loading or when skipped,
 * so callers hide nothing on a guess. `false` on error, since the page would not load either.
 */
export const useHasVoiceCalls = ({ skip }: { skip: boolean }) => {
  const months = getMonthOptions(new Date());
  const { data, error } = useQuery<{ list_voice_calls: { call_id: string }[] }>(
    GET_HAS_VOICE_CALLS,
    {
      variables: {
        start: getMonthWindow(months[months.length - 1]).start,
        end: getMonthWindow(months[0]).end,
      },
      skip,
    }
  );
  if (error) {
    return false;
  }
  return data ? data.list_voice_calls.length > 0 : undefined;
};
